import { useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormFeedback,
  FormInputWrapper,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  SmartCompanyInput,
  type RegistryCompany,
} from "@/components/SmartCompanyInput";
import { DivergenceIndicator } from "@/components/DivergenceIndicator";

const formSchema = z.object({
  name: z.string().min(1, "Company name is required"),
  uid: z.string().optional(),
  legalForm: z.string().optional(),
  street: z.string().optional(),
  city: z.string().optional(),
  zip: z.string().optional(),
  canton: z.string().optional(),
  country: z.string().default("Switzerland"),
  phone: z.string().optional(),
  email: z.string().email().optional().or(z.literal("")),
  website: z.string().url().optional().or(z.literal("")),
  taxCanton: z.string().optional(),
  vatNumber: z.string().optional(),
  taxYear: z.number().optional(),
  kontenplanTemplate: z.enum(["kmu", "kaefer", "custom"]).default("kmu"),
});

type FormValues = z.infer<typeof formSchema>;

interface CreateCompanyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCompanyCreated: (companyId: number) => void;
}

export function CreateCompanyDialog({
  open,
  onOpenChange,
  onCompanyCreated,
}: CreateCompanyDialogProps) {
  const [step, setStep] = useState(1);
  // Store the registry data when a company is selected from Zefix
  const [selectedRegistryData, setSelectedRegistryData] =
    useState<RegistryCompany | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema) as any,
    mode: "onChange", // Enable real-time validation
    defaultValues: {
      name: "",
      uid: "",
      legalForm: "",
      street: "",
      city: "",
      zip: "",
      canton: "",
      country: "Switzerland",
      phone: "",
      email: "",
      website: "",
      taxCanton: "",
      vatNumber: "",
      kontenplanTemplate: "kmu",
    },
  });

  // Handle company selection from SmartCompanyInput
  const handleCompanySelect = useCallback(
    (company: RegistryCompany) => {
      // Store the registry data for submission
      setSelectedRegistryData(company);

      // Autofill the form
      form.setValue("name", company.name);
      form.setValue("uid", company.uid);
      form.setValue("legalForm", company.legalForm || "");
      form.setValue("street", company.address?.street || "");
      form.setValue("city", company.address?.city || "");
      form.setValue("zip", company.address?.zip || "");
      form.setValue("canton", company.address?.canton || "");
      form.setValue("taxCanton", company.address?.canton || ""); // Assume tax canton is same
      if (company.vatNumber) {
        form.setValue("vatNumber", company.vatNumber);
      }

      toast.success("Company data autofilled from registry!");
    },
    [form]
  );

  // Check if a field value differs from the registry value
  const getDivergence = (field: string, currentValue: string | undefined) => {
    if (!selectedRegistryData || !currentValue) return null;

    let registryValue: string | undefined;
    switch (field) {
      case "name":
        registryValue = selectedRegistryData.name;
        break;
      case "legalForm":
        registryValue = selectedRegistryData.legalForm;
        break;
      case "uid":
        registryValue = selectedRegistryData.uid;
        break;
      case "street":
        registryValue = selectedRegistryData.address?.street;
        break;
      case "city":
        registryValue = selectedRegistryData.address?.city;
        break;
      case "zip":
        registryValue = selectedRegistryData.address?.zip;
        break;
      case "canton":
        registryValue = selectedRegistryData.address?.canton;
        break;
      default:
        return null;
    }

    if (registryValue && currentValue !== registryValue) {
      return registryValue;
    }
    return null;
  };

  const createCompanyMutation = trpc.company.create.useMutation({
    onSuccess: data => {
      toast.success("Company created successfully!");
      form.reset();
      setStep(1);
      setSelectedRegistryData(null);
      onCompanyCreated(data.id);
    },
    onError: error => {
      toast.error(`Failed to create company: ${error.message}`);
    },
  });

  const onSubmit = (values: FormValues) => {
    createCompanyMutation.mutate({
      name: values.name,
      uid: values.uid,
      legalForm: values.legalForm,
      address: {
        street: values.street,
        city: values.city,
        zip: values.zip,
        canton: values.canton,
        country: values.country,
      },
      contact: {
        phone: values.phone,
        email: values.email,
        website: values.website,
      },
      taxInfo: {
        canton: values.taxCanton,
        vatNumber: values.vatNumber,
        taxYear: values.taxYear,
      },
      kontenplanTemplate: values.kontenplanTemplate,
      // Pass registry data for divergence tracking
      registryData: selectedRegistryData
        ? {
            uid: selectedRegistryData.uid,
            name: selectedRegistryData.name,
            legalForm: selectedRegistryData.legalForm,
            address: selectedRegistryData.address
              ? {
                  street: selectedRegistryData.address.street,
                  city: selectedRegistryData.address.city,
                  zip: selectedRegistryData.address.zip,
                  canton: selectedRegistryData.address.canton,
                }
              : undefined,
            mwstNumber: selectedRegistryData.vatNumber,
            source: selectedRegistryData.source,
            fetchedAt: Date.now(),
          }
        : undefined,
    });
  };

  const handleNext = async () => {
    const fieldsToValidate =
      step === 1
        ? (["name", "legalForm", "uid"] as const)
        : step === 2
          ? (["street", "city", "zip", "canton", "country"] as const)
          : step === 3
            ? (["phone", "email", "website"] as const)
            : [];

    const isValid = await form.trigger(fieldsToValidate);
    if (isValid) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleClose = () => {
    form.reset();
    setStep(1);
    setSelectedRegistryData(null);
    onOpenChange(false);
  };

  // Watch form values for divergence indicators
  const watchedValues = form.watch();

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Create New Company</DialogTitle>
          <DialogDescription>
            Step {step} of 4:{" "}
            {step === 1
              ? "Basic Information"
              : step === 2
                ? "Address"
                : step === 3
                  ? "Contact Information"
                  : "Chart of Accounts (Kontenplan)"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Step 1: Basic Information */}
            {step === 1 && (
              <div className="space-y-4">
                {/* Smart Company Input - unified search */}
                <div className="rounded-lg border p-4 bg-muted/50 space-y-3">
                  <h3 className="font-medium text-sm">
                    Search Swiss Commercial Registry
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Enter company name, UID (CHE-XXX.XXX.XXX), or MWST number to
                    autofill
                  </p>
                  <SmartCompanyInput
                    onSelect={handleCompanySelect}
                    defaultCanton="ZH"
                    placeholder="Search by name, UID, or MWST..."
                  />
                  {selectedRegistryData && (
                    <div className="text-xs text-[var(--success)] flex items-center gap-1">
                      <span>✓</span>
                      <span>Autofilled from {selectedRegistryData.source}</span>
                    </div>
                  )}
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      Company Details
                    </span>
                  </div>
                </div>

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        Company Name *
                        <DivergenceIndicator
                          show={!!getDivergence("name", field.value)}
                          registryValue={
                            getDivergence("name", field.value) || undefined
                          }
                          fieldLabel="Company name"
                        />
                      </FormLabel>
                      <FormControl>
                        <FormInputWrapper>
                          <Input
                            placeholder="Acme AG"
                            className="pr-10"
                            {...field}
                          />
                        </FormInputWrapper>
                      </FormControl>
                      <FormFeedback
                        showSuccessOnValid
                        successMessage="Company name looks good"
                      />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="legalForm"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        Legal Form
                        <DivergenceIndicator
                          show={!!getDivergence("legalForm", field.value)}
                          registryValue={
                            getDivergence("legalForm", field.value) || undefined
                          }
                          fieldLabel="Legal form"
                        />
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select legal form" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="AG">
                            AG (Aktiengesellschaft)
                          </SelectItem>
                          <SelectItem value="GmbH">
                            GmbH (Gesellschaft mit beschränkter Haftung)
                          </SelectItem>
                          <SelectItem value="Einzelfirma">
                            Einzelfirma (Sole Proprietorship)
                          </SelectItem>
                          <SelectItem value="Kollektivgesellschaft">
                            Kollektivgesellschaft (General Partnership)
                          </SelectItem>
                          <SelectItem value="Kommanditgesellschaft">
                            Kommanditgesellschaft (Limited Partnership)
                          </SelectItem>
                          <SelectItem value="Genossenschaft">
                            Genossenschaft (Cooperative)
                          </SelectItem>
                          <SelectItem value="Verein">
                            Verein (Association)
                          </SelectItem>
                          <SelectItem value="Stiftung">
                            Stiftung (Foundation)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="uid"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        UID (Unternehmens-Identifikationsnummer)
                        <DivergenceIndicator
                          show={!!getDivergence("uid", field.value)}
                          registryValue={
                            getDivergence("uid", field.value) || undefined
                          }
                          fieldLabel="UID"
                        />
                      </FormLabel>
                      <FormControl>
                        <FormInputWrapper>
                          <Input
                            placeholder="CHE-123.456.789"
                            className="pr-10"
                            {...field}
                          />
                        </FormInputWrapper>
                      </FormControl>
                      <FormDescription>
                        Swiss business identification number (e.g.,
                        CHE-123.456.789)
                      </FormDescription>
                      <FormFeedback
                        showSuccessOnValid
                        successMessage="Valid UID format"
                      />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Step 2: Address */}
            {step === 2 && (
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="street"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        Street Address
                        <DivergenceIndicator
                          show={!!getDivergence("street", field.value)}
                          registryValue={
                            getDivergence("street", field.value) || undefined
                          }
                          fieldLabel="Street"
                        />
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="Bahnhofstrasse 1" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="zip"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          Postal Code
                          <DivergenceIndicator
                            show={!!getDivergence("zip", field.value)}
                            registryValue={
                              getDivergence("zip", field.value) || undefined
                            }
                            fieldLabel="Postal code"
                          />
                        </FormLabel>
                        <FormControl>
                          <Input placeholder="8001" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          City
                          <DivergenceIndicator
                            show={!!getDivergence("city", field.value)}
                            registryValue={
                              getDivergence("city", field.value) || undefined
                            }
                            fieldLabel="City"
                          />
                        </FormLabel>
                        <FormControl>
                          <Input placeholder="Zürich" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="canton"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        Canton
                        <DivergenceIndicator
                          show={!!getDivergence("canton", field.value)}
                          registryValue={
                            getDivergence("canton", field.value) || undefined
                          }
                          fieldLabel="Canton"
                        />
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select canton" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="ZH">Zürich (ZH)</SelectItem>
                          <SelectItem value="BE">Bern (BE)</SelectItem>
                          <SelectItem value="LU">Luzern (LU)</SelectItem>
                          <SelectItem value="UR">Uri (UR)</SelectItem>
                          <SelectItem value="SZ">Schwyz (SZ)</SelectItem>
                          <SelectItem value="OW">Obwalden (OW)</SelectItem>
                          <SelectItem value="NW">Nidwalden (NW)</SelectItem>
                          <SelectItem value="GL">Glarus (GL)</SelectItem>
                          <SelectItem value="ZG">Zug (ZG)</SelectItem>
                          <SelectItem value="FR">Fribourg (FR)</SelectItem>
                          <SelectItem value="SO">Solothurn (SO)</SelectItem>
                          <SelectItem value="BS">Basel-Stadt (BS)</SelectItem>
                          <SelectItem value="BL">
                            Basel-Landschaft (BL)
                          </SelectItem>
                          <SelectItem value="SH">Schaffhausen (SH)</SelectItem>
                          <SelectItem value="AR">
                            Appenzell Ausserrhoden (AR)
                          </SelectItem>
                          <SelectItem value="AI">
                            Appenzell Innerrhoden (AI)
                          </SelectItem>
                          <SelectItem value="SG">St. Gallen (SG)</SelectItem>
                          <SelectItem value="GR">Graubünden (GR)</SelectItem>
                          <SelectItem value="AG">Aargau (AG)</SelectItem>
                          <SelectItem value="TG">Thurgau (TG)</SelectItem>
                          <SelectItem value="TI">Ticino (TI)</SelectItem>
                          <SelectItem value="VD">Vaud (VD)</SelectItem>
                          <SelectItem value="VS">Valais (VS)</SelectItem>
                          <SelectItem value="NE">Neuchâtel (NE)</SelectItem>
                          <SelectItem value="GE">Genève (GE)</SelectItem>
                          <SelectItem value="JU">Jura (JU)</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country</FormLabel>
                      <FormControl>
                        <Input {...field} disabled />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Step 3: Contact Information */}
            {step === 3 && (
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone</FormLabel>
                      <FormControl>
                        <Input placeholder="+41 44 123 45 67" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <FormInputWrapper>
                          <Input
                            type="email"
                            placeholder="info@company.ch"
                            className="pr-10"
                            {...field}
                          />
                        </FormInputWrapper>
                      </FormControl>
                      <FormFeedback
                        showSuccessOnValid
                        successMessage="Valid email address"
                      />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="website"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Website</FormLabel>
                      <FormControl>
                        <FormInputWrapper>
                          <Input
                            type="url"
                            placeholder="https://www.company.ch"
                            className="pr-10"
                            {...field}
                          />
                        </FormInputWrapper>
                      </FormControl>
                      <FormFeedback
                        showSuccessOnValid
                        successMessage="Valid URL"
                      />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="taxCanton"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tax Canton</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select tax canton" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="ZH">Zürich (ZH)</SelectItem>
                          <SelectItem value="BE">Bern (BE)</SelectItem>
                          <SelectItem value="LU">Luzern (LU)</SelectItem>
                          <SelectItem value="AG">Aargau (AG)</SelectItem>
                          <SelectItem value="GE">Genève (GE)</SelectItem>
                          <SelectItem value="VD">Vaud (VD)</SelectItem>
                          <SelectItem value="TI">Ticino (TI)</SelectItem>
                          <SelectItem value="SG">St. Gallen (SG)</SelectItem>
                          <SelectItem value="BS">Basel-Stadt (BS)</SelectItem>
                          <SelectItem value="BL">
                            Basel-Landschaft (BL)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="vatNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>VAT Number</FormLabel>
                      <FormControl>
                        <Input placeholder="CHE-123.456.789 MWST" {...field} />
                      </FormControl>
                      <FormDescription>
                        Swiss VAT registration number
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Step 4: Kontenplan Selection */}
            {step === 4 && (
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="kontenplanTemplate"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel>
                        Chart of Accounts Template (Kontenplan)
                      </FormLabel>
                      <FormDescription>
                        Choose a standard Swiss accounting template or start
                        with a custom plan.
                      </FormDescription>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          value={field.value}
                          className="flex flex-col space-y-2"
                        >
                          <div className="flex items-start space-x-3 space-y-0 rounded-md border p-4">
                            <RadioGroupItem value="kmu" />
                            <div className="space-y-1 leading-none">
                              <FormLabel className="font-medium">
                                KMU (Recommended)
                              </FormLabel>
                              <FormDescription>
                                Comprehensive chart of accounts for small and
                                medium enterprises. Includes 100+ accounts
                                following Swiss GAAP FER standards with proper
                                asset (1xxx), liability (2xxx), equity (28xx),
                                revenue (3xxx), and expense (4-6xxx) structure.
                                Best for most Swiss businesses.
                              </FormDescription>
                            </div>
                          </div>

                          <div className="flex items-start space-x-3 space-y-0 rounded-md border p-4">
                            <RadioGroupItem value="kaefer" />
                            <div className="space-y-1 leading-none">
                              <FormLabel className="font-medium">
                                Käfer
                              </FormLabel>
                              <FormDescription>
                                Simplified chart of accounts for very small
                                businesses and sole proprietors. Contains only
                                essential accounts for basic bookkeeping. Ideal
                                for freelancers and micro-enterprises.
                              </FormDescription>
                            </div>
                          </div>

                          <div className="flex items-start space-x-3 space-y-0 rounded-md border p-4">
                            <RadioGroupItem value="custom" />
                            <div className="space-y-1 leading-none">
                              <FormLabel className="font-medium">
                                Custom
                              </FormLabel>
                              <FormDescription>
                                Start with an empty chart of accounts and create
                                your own structure. Choose this if you have
                                specific requirements or want to import an
                                existing account plan.
                              </FormDescription>
                            </div>
                          </div>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            <DialogFooter className="gap-2">
              {step > 1 && (
                <Button type="button" variant="outline" onClick={handleBack}>
                  Back
                </Button>
              )}
              {step < 4 ? (
                <Button type="button" onClick={handleNext}>
                  Next
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={createCompanyMutation.isPending}
                >
                  {createCompanyMutation.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Create Company
                </Button>
              )}
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
